import { inject } from '@angular/core';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from '@kinet-open-network/client/shared/data-access/state';

import { IAddItemPrice } from '@kinet-open-network/shared/data-access/model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { groupBy } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { ItemPrice } from './item-price.model';
import { ItemPricesService } from './item-prices.service';

export interface ItemPricesState {
  /**
   * prices keyed by type string, e.g. { withCommitment: [...], withoutCommitment: [...] }
   */
  prices: Record<string, ItemPrice[]>;
}

const initialState: ItemPricesState = {
  prices: {},
};

export const ItemPricesStore = signalStore(
  withState(initialState),
  withCallState(),
  withMethods((store, service = inject(ItemPricesService)) => ({
    getPrices(type: string): ItemPrice[] {
      return store.prices()[type] ?? [];
    },

    async loadPrices(entityId: string): Promise<void> {
      patchState(store, setLoading());
      try {
        const prices = await firstValueFrom(service.getPrices(entityId));

        patchState(
          store,
          {
            prices: {
              ...groupBy(prices, (item) => item.type),
            },
          },
          setLoaded(),
        );
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async addPrice(
      entityId: string,
      type: string,
      data: IAddItemPrice,
    ): Promise<void> {
      patchState(store, setLoading());
      try {
        const prices = await firstValueFrom(
          service.addPrice(entityId, type, data),
        );
        patchState(
          store,
          (state) => ({
            prices: { ...state.prices, [type]: prices },
          }),
          setLoaded(),
        );
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async editPrice(
      entityId: string,
      type: string,
      priceId: string,
      data: IAddItemPrice,
    ): Promise<void> {
      patchState(store, setLoading());
      try {
        const prices = await firstValueFrom(
          service.editPrice(entityId, type, priceId, data),
        );
        patchState(
          store,
          (state) => ({
            prices: { ...state.prices, [type]: prices },
          }),
          setLoaded(),
        );
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async removePrice(
      entityId: string,
      type: string,
      priceId: string,
    ): Promise<void> {
      patchState(store, setLoading());
      try {
        const prices = await firstValueFrom(
          service.removePrice(entityId, type, priceId),
        );
        patchState(
          store,
          (state) => ({
            prices: { ...state.prices, [type]: prices },
          }),
          setLoaded(),
        );
      } catch (error) {
        patchState(store, setError(error));
      }
    },
  })),
);
